// KenoGame.jsx
import React, { useState, useEffect, useRef } from "react";
import "./KenoGame.css";
import { api, socket } from "../../api";
import { parseJWT } from "../../utils/jwtUtils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import payoutTable from "./payoutTable";
import KenoRule from "./KenoRule";
import KenoRightSidebar from "./KenoRightSidebar";
import KenoLeftSidebar from "./KenoLeftSidebar";

const MAX_SELECTION = 10;
const ROUND_TIME = 60;

const KenoGame = () => {
  // useEffect(() => {
  //   document.documentElement.style.fontSize = "14px";

  //   return () => {
  //     document.documentElement.style.fontSize = "";
  //   };
  // }, []);

  // ---------------- STATE ----------------
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [matchedNumbers, setMatchedNumbers] = useState([]);
  const [timer, setTimer] = useState(ROUND_TIME);
  const [gameStarted, setGameStarted] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [resultText, setResultText] = useState("");
  const [gameId, setGameId] = useState(1000);
  const [possibleWin, setPossibleWin] = useState(0);
  const [gamePhase, setGamePhase] = useState("waiting");
  const [betPerBall, setBetPerBall] = useState(1);
  const [balance, setBalance] = useState(0);
  const [allBets, setAllBets] = useState([]);
  const [roundResult, setRoundResult] = useState(null);
  const [winAmount, setWinAmount] = useState(0);
  const [recentRounds, setRecentRounds] = useState([]);
  const [hotNumbers, setHotNumbers] = useState([]);
  const [coldNumbers, setColdNumbers] = useState([]);

  const timerRef = useRef(null);
  const selectedNumbersRef = useRef([]);
  const currentSelected = selectedNumbersRef.current;
  const totalBetAmount = betPerBall;

  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;

  const userInfo = parseJWT(token?.token);

  const userId = userInfo?.id;
  const phone = userInfo?.phone;

  useEffect(() => {
    if (userInfo?.phone) {
      //   console.log("Requesting balance for phone:", userInfo.phone);
      socket.emit("getBalance", { phone: phone });

      socket.on("balanceResponse", (data) => {
        //  console.log("Received balance response from server:", data);
        if (data.success) {
          setBalance(data.balance);
        }
      });
    }

    return () => {
      socket.off("balanceResponse");
    };
  }, [userInfo?.phone]);
  // console.log("user info : ", userInfo);
  useEffect(() => {
    selectedNumbersRef.current = selectedNumbers;
  }, [selectedNumbers]);

  // ---------------- SELECT NUMBER ----------------
  const handleSelectNumber = (number) => {
    if (drawing) return;

    setSelectedNumbers((prev) => {
      if (prev.includes(number)) {
        return prev.filter((n) => n !== number);
      }

      if (prev.length >= MAX_SELECTION) return prev;

      return [...prev, number];
    });
  };

  const resetRoundState = () => {
    setDrawnNumbers([]);
    setMatchedNumbers([]);
    setResultText("");
    setPossibleWin(0);

    // IMPORTANT
    setSelectedNumbers([]);

    setDrawing(false);
    setGamePhase("betting");
  };

  useEffect(() => {
    if (userId) {
      socket.emit("joinGame", {
        userId,
        phone,
      });
    }
    // =========================================
    // JOINED
    // =========================================
    socket.on("joined", (data) => {
      // =====================================
      // CURRENT ROUND
      // =====================================

      if (data.currentRound) {
        setGameId(data.currentRound.roundId);

        setTimer(data.currentRound.timer ?? ROUND_TIME);

        if (data.currentRound.status === "waiting") {
          setGamePhase("betting");
        } else {
          setGamePhase(data.currentRound.status);
        }
      }
    });

    // =========================================
    // ROUND STARTED
    // =========================================
    socket.on("roundStarted", (data) => {
      setGameId(data.roundId);

      setTimer(data.timer);
      // RESET OLD ROUND
      resetRoundState();

      setGamePhase("betting");

      setDrawing(false);
    });

    // =========================================
    // TIMER UPDATE
    // =========================================
    socket.on("timerUpdate", (time) => {
      //  console.log("⏳ TIMER:", time);

      setTimer(time);
    });

    // =========================================
    // DRAW STARTED
    // =========================================
    socket.on("drawStarted", () => {
      //  console.log("🎲 DRAW STARTED");
      setGamePhase("drawing");
      setDrawing(true);
      setDrawnNumbers([]);
    });

    // =========================================
    // EACH BALL DRAWN
    // =========================================
    socket.on("numberDrawn", (data) => {
      //   console.log("🎱 DRAWN NUMBER TYPE:", typeof data.number, data.number);
      setDrawnNumbers((prev) => [...prev, data.number]);
    });

    // =========================================
    // BET ACCEPTED
    // =========================================
    socket.on("betPlaced", (data) => {
      // UPDATE BALANCE INSTANTLY
      if (data.balance !== undefined) {
        setBalance(data.balance);
      }

      setResultText("");
      setMatchedNumbers([]);
      // RESET SELECTED BALLS
      setSelectedNumbers([]);

      toast.success(`Bet placed successfully!`, {
        position: "top-center",
        autoClose: 2000,
      });
    });

    // =========================================
    // PLAYER BET EVENT
    // =========================================
    socket.on("playerBet", (data) => {});

    // =========================================
    // BET ERROR
    // =========================================
    socket.on("betError", (err) => {
      toast.error(err.message || "Bet failed", {
        position: "top-center",
        autoClose: 3000,
      });
    });

    // =========================================
    // LIVE GAME BETS
    // =========================================
    socket.on("liveGameBets", (data) => {
      setAllBets(data || []);
    });

    // =========================================
    // ROUND FINISHED
    // =========================================
    socket.on("roundFinished", (data) => {
      setDrawing(false);
      setRecentRounds((prev) => {
        const updated = [...prev, data.numbers];

        // Keep last 30 rounds
        if (updated.length > 30) {
          updated.shift();
        }

        return updated;
      });
      if (phone) {
        socket.emit("getBalance", { phone });
      }

      const currentSelected = selectedNumbersRef.current;

      const selected = currentSelected.map(Number);

      const drawn = data.numbers.map(Number);

      const matched = drawn.filter((n) => selected.includes(n));

      setMatchedNumbers(matched);
      setDrawnNumbers(drawn);

      const myResult = data.playerResults?.[userId];

      if (myResult && myResult.status === "won") {
        setRoundResult("win");

        setWinAmount(Number(myResult.winAmount || 0));

        setResultText(`${myResult.matches} Match`);
      } else {
        setRoundResult("lost");
        setWinAmount(0);

        setResultText(matched.length ? `${matched.length} Match` : "No Match");
      }

      setTimeout(() => {
        resetRoundState();

        setRoundResult(null);
        setWinAmount(0);
        setGamePhase("finished");
      }, 1000);
    });

    socket.on("hotColdUpdate", (data) => {
      setHotNumbers((data.hotNumbers || []).map((item) => item.number));

      setColdNumbers((data.coldNumbers || []).map((item) => item.number));
    });
    //=======================================
    // BETS UPDATED
    // =========================================
    socket.on("betsUpdated", (data) => {});
    // =========================================
    // CLEANUP
    // =========================================
    return () => {
      // console.log("🧹 CLEANING SOCKET EVENTS");
      socket.off("balanceResponse");

      socket.off("joined");

      socket.off("roundStarted");

      socket.off("timerUpdate");

      socket.off("drawStarted");

      socket.off("numberDrawn");

      socket.off("roundFinished");
      socket.off("hotColdUpdate");
      socket.off("betPlaced");

      socket.off("betError");

      socket.off("playerBet");

      socket.off("betsUpdated");

      socket.off("liveGameBets");

      socket.off("connect");

      socket.off("disconnect");
    };
  }, []);
  // =========================================

  // ---------------- RESET GAME ----------------
  const resetGame = () => {
    setSelectedNumbers([]);
    setDrawnNumbers([]);
    setMatchedNumbers([]);
    setResultText("");
    setTimer(ROUND_TIME);
    setGameStarted(false);
    setGameId((prev) => prev + 1);
  };

  // ---------------- CLEANUP ----------------
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  // ---------------- BET FUNCTIONS ----------------
  const increaseBet = () => {
    setBetPerBall((prev) => prev + 1);
  };

  const decreaseBet = () => {
    setBetPerBall((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const doubleBet = () => {
    setBetPerBall((prev) => prev * 2);
  };

  const maxBet = () => {
    setBetPerBall(20);
  };

  useEffect(() => {
    const selectedCount = selectedNumbers.length;

    if (!selectedCount) {
      setPossibleWin(0);
      return;
    }

    const payouts = payoutTable[selectedCount];

    if (!payouts) {
      setPossibleWin(0);
      return;
    }

    const highestMultiplier = Math.max(...Object.values(payouts));

    setPossibleWin(highestMultiplier * betPerBall);
  }, [selectedNumbers, betPerBall]);

  const placeBet = () => {
    if (!socket.connected) {
      return toast.error("Server is not connected. Please try again.");
    }
    if (!userId) {
      return toast.error("Login required");
    }

    if (!selectedNumbers.length) {
      return toast.error("Select numbers first");
    }

    if (selectedNumbers.length > 10) {
      return toast.error("Maximum 10 numbers allowed");
    }

    if (betPerBall <= 0) {
      return toast.error("Invalid bet amount");
    }
    if (totalBetAmount > 50) {
      return toast.error("Maximum Bet Amount is 50 birr");
    }

    socket.emit("placeBet", {
      userId,
      numbers: selectedNumbers.map(Number),
      betPerBall,
      betAmount: totalBetAmount,
    });
  };

  const isStopBet = drawing;

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeHeaderTab, setActiveHeaderTab] = useState("rules");

  // =========================================
  // ALL MY BET NUMBERS IN CURRENT ROUND
  // =========================================

  const myBets = allBets.filter((bet) => Number(bet.userId) === Number(userId));

  // COMBINE ALL NUMBERS
  let allMyNumbers = [];

  myBets.forEach((bet) => {
    let numbers = bet.numbers || [];

    // HANDLE JSON STRING
    if (typeof numbers === "string") {
      try {
        numbers = JSON.parse(numbers);
      } catch {
        numbers = [];
      }
    }

    // ENSURE ARRAY
    if (!Array.isArray(numbers)) {
      numbers = [];
    }

    // PUSH ALL NUMBERS
    allMyNumbers.push(...numbers.map(Number));
  });

  // REMOVE DUPLICATES
  const myNumbersSet = new Set(allMyNumbers);
  // console.log("Numbers list : ", myNumbersSet);
  return (
    <>
      {/* <div className="fixed left-0 right-0 top-0 z-50 bg-gradient-to-l from-violet-900 via-violet-800 to-violet-900 ">
        <Navbar2 />
      </div> */}

      <div className="keno_game">
        {/* MOBILE ROTATE */}
        <div className="mobile_rotate" id="mobile_rotate">
          <div className="mobile_rotate_wrap">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mobile_rotate_svg"
              viewBox="0 0 200 200"
            >
              <path
                fill="#FFFFFF"
                stroke="none"
                d="M 53.15 184.95Q 61.45 191.6 71.4 191.6L 71.4 199.55 85.55 185.8 71.4 171.9 71.4 180.4Q 64.1 181.15 57.7 174.5 55.35 172 53.95 168.9 52.35 165.3 51.9 160.8L 41.8 160.8Q 41.8 165 42.75 168.9 45.1 177.9 53.15 184.95M 118.6 37.6L 81.8 37.6Q 76 37.6 71.85 41.7 67.75 45.8 67.75 51.65L 67.75 148Q 67.75 153.85 71.85 158 76 162.1 81.8 162.1L 118.6 162.1Q 124.4 162.1 128.5 158 132.6 153.85 132.6 148L 132.6 51.65Q 132.6 45.8 128.5 41.7 124.4 37.6 118.6 37.6M 102.1 47.05Q 102.95 47.9 102.95 49.1 102.95 50.25 102.1 51.1 101.25 51.95 100.05 51.95 98.85 51.95 98 51.1 97.15 50.25 97.15 49.1 97.15 47.9 98 47.05 98.85 46.2 100.05 46.2 101.25 46.2 102.1 47.05M 95.7 145.5L 104.3 145.5Q 105.6 145.5 105.6 146.85L 105.6 155.4Q 105.6 156.75 104.3 156.75L 95.7 156.75Q 94.35 156.75 94.35 155.4L 94.35 146.85Q 94.35 145.5 95.7 145.5M 73.35 58.65L 126.8 58.65 126.8 140.9 73.35 140.9 73.35 58.65M 146.9 15Q 138.55 8.4 128.65 8.4L 128.65 0.4 114.45 14.15 128.65 28.05 128.65 19.55Q 136 18.8 142.35 25.5 147.35 30.8 148.1 39.2L 158.25 39.2Q 158.4 25.05 146.9 15 Z"
              />
            </svg>
          </div>
        </div>

        {/* SOCKET STATUS */}
        <div className="socket_status" id="socket_status">
          <div className="socket_status_text" id="socket_status_text">
            Session expired. Please refresh the page.
          </div>
        </div>

        {/* DESKTOP BG */}
        <div className="desktop_background"></div>

        {/* MAIN WRAPPER */}
        <div
          id="global_conteiner"
          className={`home fairness global_wrapper ${
            gamePhase === "drawing" ? "stopbet" : ""
          }`}
        >
          {/* HEADER */}
          <header className="header">
            <div className="header_content">
              <img
                className="header_logo_mobile"
                alt="Logo"
                src="/assets/logo_m-BtfePcJR.png"
              />
              <div className="header_center">
                <div className="header_center_left">
                  <span className="header_credit" id="my_credit">
                    {balance} <span>ETB</span>
                  </span>
                </div>
                <div className="header_center_right">
                  <div className="game_id" data-id="">
                    ID: <span className="game_id_num">{gameId}</span>
                  </div>
                </div>
              </div>
              <div className="header_right">
                <span className="settings_icon">
                  <img
                    className="menu_open"
                    alt="Menu"
                    src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20width='15'%20height='14'%20viewBox='0%200%2015%2014'%3e%3cg%20id='Group_4538'%20data-name='Group%204538'%20transform='translate(-342.944%20-6.121)'%20%3e%3crect%20id='Rectangle_434'%20data-name='Rectangle%20434'%20width='15'%20height='2'%20rx='1'%20transform='translate(342.944%206.121)'%20fill='%2371ce96'/%3e%3crect%20id='Rectangle_435'%20data-name='Rectangle%20435'%20width='15'%20height='2'%20rx='1'%20transform='translate(342.944%2018.121)'%20fill='%2371ce96'/%3e%3crect%20id='Rectangle_436'%20data-name='Rectangle%20436'%20width='15'%20height='2'%20rx='1'%20transform='translate(342.944%2012.121)'%20fill='%2371ce96'/%3e%3c/g%3e%3c/svg%3e"
                    onClick={() => setMenuOpen(true)}
                  />
                </span>
              </div>
              <div className="clear" onClick={() => setMenuOpen(false)}></div>
            </div>
          </header>

          <KenoRule
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            activeHeaderTab={activeHeaderTab}
            setActiveHeaderTab={setActiveHeaderTab}
          />
          {/* CONTENT */}
          <div className="content_super_wrapper">
            {/* LEFT SIDE */}
            <KenoLeftSidebar
              balance={balance}
              gameId={gameId}
              drawnNumbers={drawnNumbers}
            />

            {/* CENTER GAME */}
            <div className={`canvas_wrap ${isStopBet ? "stopbet" : ""}`}>
              <div className="side_cont_path">
                <img
                  className="header_logo"
                  alt="Logo"
                  src="/assets/logo-DAz_dQ2-.png"
                />

                <div className="game_timer">
                  00 :{" "}
                  <span className="game_timer_sec">
                    {String(timer ?? 0).padStart(2, "0")}
                  </span>
                </div>

                <div className="balls_counter">
                  <span className="balls_counter_num">
                    {drawnNumbers.length}
                  </span>{" "}
                  / <span>20</span>
                </div>
                <div className="menu_icon">
                  <img
                    className="menu_open"
                    alt="Menu"
                    src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20width='15'%20height='14'%20viewBox='0%200%2015%2014'%3e%3cg%20id='Group_4538'%20data-name='Group%204538'%20transform='translate(-342.944%20-6.121)'%20%3e%3crect%20id='Rectangle_434'%20data-name='Rectangle%20434'%20width='15'%20height='2'%20rx='1'%20transform='translate(342.944%206.121)'%20fill='%2371ce96'/%3e%3crect%20id='Rectangle_435'%20data-name='Rectangle%20435'%20width='15'%20height='2'%20rx='1'%20transform='translate(342.944%2018.121)'%20fill='%2371ce96'/%3e%3crect%20id='Rectangle_436'%20data-name='Rectangle%20436'%20width='15'%20height='2'%20rx='1'%20transform='translate(342.944%2012.121)'%20fill='%2371ce96'/%3e%3c/g%3e%3c/svg%3e"
                  />
                </div>
                {/* GAME ANIMATION */}
                <div className="canvas_wrap_inn">
                  <div
                    className={`game_result_popup ${
                      roundResult === "win" ? "lost" : ""
                    }`}
                  >
                    <div className="game_result_popup_win">
                      <div
                        className="game_result_popup_win_ttl"
                        id="trs_you_win"
                      >
                        YOU WIN
                      </div>
                      <div className="game_result_popup_win_amount">
                        {winAmount.toFixed(2)} Birr
                      </div>
                    </div>
                    <div className="game_result_popup_lost" id="trs_try">
                      TRY AGAIN
                    </div>
                  </div>
                  <div
                    id="game_animation"
                    style={{
                      padding: "5px 0 38%",
                      margin: "0 auto",
                      height: "auto",
                    }}
                  >
                    <div
                      className={`game_animation_row1 ${
                        drawnNumbers.length > 10 ? "down" : ""
                      }`}
                    >
                      {drawnNumbers.slice(0, 10).map((num, index) => {
                        const currentNumber = Number(num);
                        // MATCH CHECK
                        const isMatched = myNumbersSet.has(currentNumber);
                        // LAST DRAWN BALL
                        const isLatest =
                          Number(currentNumber) ===
                          Number(drawnNumbers[drawnNumbers.length - 1]);
                        //  console.log("is last number : ", isLatest);
                        return (
                          <div
                            key={`top-${currentNumber}-${index}`}
                            className={[
                              "anim_ball_cont",
                              `anim_ball_cont${index + 1}`,
                              "move",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <div
                              className={[
                                "anim_ball",
                                currentNumber ? "show" : "",
                                isMatched ? "active" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              <i></i>
                              <span>{currentNumber}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* BOTTOM ROW */}
                    <div className="game_animation_row2">
                      {drawnNumbers.slice(10, 20).map((num, index) => {
                        const currentNumber = Number(num);

                        const isMatched = myNumbersSet.has(currentNumber);

                        const isLatest =
                          Number(currentNumber) ===
                          Number(drawnNumbers[drawnNumbers.length - 1]);

                        return (
                          <div
                            key={`bottom-${currentNumber}-${index}`}
                            className={[
                              "anim_ball_cont",
                              `anim_ball_cont${index + 11}`,
                              "move",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <div
                              className={[
                                "anim_ball",
                                currentNumber ? "show" : "",
                                isMatched ? "active" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            >
                              <i></i>
                              <span>{currentNumber}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div
                  id="tickets_carousel"
                  className="owl-carousel owl-theme owl-loaded owl-drag"
                >
                  <div className="owl-stage-outer">
                    <div
                      className="owl-stage"
                      style={{
                        transition: "transform 0.25s ease-out",
                        width: "373px",
                        transform: "translate3d(1px, 0px, 0px)",
                        willChange: "auto",
                        cursor: "grab",
                        touchAction: "none",
                        userSelect: "none",
                      }}
                    >
                      <div
                        className="owl-item active center"
                        style={{
                          width: "363px",
                          marginRight: "10px",
                        }}
                      >
                        <div
                          className={`bet_selections ${
                            selectedNumbers.length ? "selected" : ""
                          }`}
                          id="bet_selections"
                        >
                          <div className="ticket_help">?</div>

                          <div className="ticket_helptext">
                            <div className="ticket_helptext1" id="trs_tkth1">
                              Choose 10 numbers
                            </div>

                            <div className="ticket_helptext2" id="trs_tkth2">
                              From 1 to 80
                            </div>
                          </div>

                          {/* SHOW ONLY WHEN PLAYER SELECTS */}
                          {selectedNumbers.length > 0 && (
                            <>
                              {/* TOP */}
                              <div className="ticket_sel_cont_ttl">
                                <span className="tic_curr_n">
                                  {/* {selectedNumbers.length} */}1
                                </span>

                                <span className="ticket_sel_cont_ttl_txt">
                                  Possible win
                                </span>

                                <span className="tic_s_win">{possibleWin}</span>
                              </div>

                              {/* MATCH ROW */}
                              <div className="ticket_match_tbl1">
                                <div className="ticket_match_tbl_itm">
                                  Match
                                </div>

                                {[...Array(7)].map((_, i) => {
                                  const payouts =
                                    payoutTable[selectedNumbers.length] || {};

                                  const matchKey = Object.keys(payouts)[i];

                                  return (
                                    <div
                                      key={i}
                                      className={`ticket_match_tbl_itm ombl ${
                                        matchKey ? "selected" : ""
                                      } ${
                                        i === Object.keys(payouts).length - 1
                                          ? "lasone"
                                          : ""
                                      }`}
                                    >
                                      {matchKey || ""}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* PAYOUT ROW */}
                              <div className="ticket_match_tbl2">
                                <div className="ticket_match_tbl_itm">Pays</div>

                                {[...Array(7)].map((_, i) => {
                                  const payouts =
                                    payoutTable[selectedNumbers.length] || {};

                                  const payout = Object.values(payouts)[i];

                                  return (
                                    <div
                                      key={i}
                                      className={`ticket_match_tbl_itm ombl ${
                                        payout ? "selected" : ""
                                      } ${
                                        i === Object.values(payouts).length - 1
                                          ? "lasone"
                                          : ""
                                      }`}
                                    >
                                      {payout ? `x${payout}` : ""}
                                    </div>
                                  );
                                })}
                              </div>

                              {/* SELECTED NUMBERS */}
                              <div className="ticket_sel_tbl">
                                {[...Array(10)].map((_, index) => (
                                  <div
                                    key={index}
                                    className={`tic_b ${
                                      selectedNumbers[index] ? "selected" : ""
                                    }`}
                                  >
                                    {selectedNumbers[index] || ""}
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="owl-nav disabled">
                    <button
                      type="button"
                      role="presentation"
                      className="owl-prev"
                    >
                      <span aria-label="Previous">‹</span>
                    </button>

                    <button
                      type="button"
                      role="presentation"
                      className="owl-next"
                    >
                      <span aria-label="Next">›</span>
                    </button>
                  </div>

                  <div className="owl-dots disabled"></div>
                </div>
                {/* BET BOARD */}
                <div className="bet_board_cont" id="bet_board_cont">
                  {[...Array(8)].map((_, row) => (
                    <div className="bet_board_row" key={row}>
                      {[...Array(10)].map((_, col) => {
                        const number = row * 10 + col + 1;

                        const isSelected = selectedNumbers.includes(number);

                        const isDrawn = drawnNumbers.includes(number);

                        const isMatched = matchedNumbers.includes(number);
                        return (
                          <div
                            key={number}
                            id={`ka${number}`}
                            onClick={() => handleSelectNumber(number)}
                            className={`
    bet_board_itm 
    ${hotNumbers.includes(number) ? "min" : ""}
    ${coldNumbers.includes(number) ? "max" : ""}
    ${drawnNumbers.includes(number) ? "show" : ""}
    ${selectedNumbers.includes(number) ? "selected" : ""}
  `}
                          >
                            {number}
                            <div className="bet_board_itm_left"></div>
                            {number > 10 && (
                              <>
                                <div className="bet_board_itm_top"></div>
                                <div className="bet_board_itm_qross"></div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* BET INPUT */}
                <div className="bet_board">
                  <div className="bet_input">
                    <div className="bet_input_row">
                      <div className="bet_input_top">
                        <div className="bet_input_ui">
                          <span
                            className="bet_input_btn_m"
                            onClick={decreaseBet}
                          >
                            <img
                              alt="Minus"
                              src="https://cdn-atlas-v.com/games/keno/img/mns.svg"
                            />
                          </span>

                          <input
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="bet_input_bar"
                            autoComplete="new-password"
                            type="number"
                            value={betPerBall}
                            onChange={(e) =>
                              setBetPerBall(Number(e.target.value))
                            }
                          />

                          <span
                            className="bet_input_btn_m"
                            onClick={increaseBet}
                          >
                            <img
                              alt="Plus"
                              src="https://cdn-atlas-v.com/games/keno/img/pls.svg"
                            />
                          </span>
                        </div>

                        <div
                          className="bet_input_btn double_bet"
                          onClick={doubleBet}
                        >
                          <span>X2</span>
                        </div>

                        <div className="bet_input_btn" onClick={maxBet}>
                          <span>MAX</span>
                        </div>
                      </div>
                    </div>

                    <div className="bet_input_row">
                      <div className="bet_set_btn" onClick={placeBet}>
                        <div className="bet_set_btn_txt">BET</div>

                        <div className="bet_set_btn_wait">WAIT...</div>

                        <div className="bet_set_btn_accept">
                          <img
                            alt="Checked"
                            src="https://cdn-atlas-v.com/games/keno/img/check.svg"
                          />

                          <span>BET ACCEPTED</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <KenoRightSidebar
              betPerBall={betPerBall}
              setBetPerBall={setBetPerBall}
              increaseBet={increaseBet}
              decreaseBet={decreaseBet}
              doubleBet={doubleBet}
              maxBet={maxBet}
              placeBet={placeBet}
              userId={userId}
              drawnNumbers={drawnNumbers}
            />
          </div>

          {/* WARNING */}
          <div className="warning_alert_popup" style={{ display: "none" }}>
            <div className="warning_alert_popup_cont">
              <div className="close_btn"></div>

              <h1 className="warning_alert_popup_info">
                You lost your connection.
                <br />
                Please reconnect.
              </h1>
            </div>
          </div>
        </div>
        {/* ✅ Toast Container */}
        <ToastContainer
          position="top-right"
          autoClose={500}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
      </div>

      {/* <Footer /> */}
    </>
  );
};

export default KenoGame;
